import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { formatDate } from "@/lib/utils";
import { School, Calendar, ChevronDown, ChevronUp, ExternalLink, ArrowRight } from "lucide-react";

interface RecruitingEvent {
  id: string;
  title: string;
  description?: string;
  date: string;
  sportType?: string;
  division?: string;
  isNcaaEvent?: boolean;
  isCurrent?: boolean;
}

type PeriodStatusType = "contact" | "quiet" | "dead" | "evaluation" | "shutdown";

interface PeriodStatusCard {
  id: string;
  periodType: PeriodStatusType;
  label: string;
  status: "current" | "upcoming" | "inactive";
  primaryText: string;
  rangeText: string;
  secondaryText: string;
}

interface RecruitingTimelineProps {
  events: RecruitingEvent[];
  athleteSport?: string;
  division?: string;
  detailed?: boolean;
  periodStatus?: PeriodStatusCard[];
  sourceName?: string;
  sourceUrl?: string;
  emptyMessage?: string;
}

const statusBadgeClass: Record<PeriodStatusType, string> = {
  contact: "bg-emerald-100 text-emerald-700",
  quiet: "bg-amber-100 text-amber-700",
  dead: "bg-rose-100 text-rose-700",
  evaluation: "bg-sky-100 text-sky-700",
  shutdown: "bg-violet-100 text-violet-700",
};

const periodConfig: Array<{ type: PeriodStatusType; label: string; keyword: string }> = [
  { type: "contact", label: "Contact Period", keyword: "contact period" },
  { type: "quiet", label: "Quiet Period", keyword: "quiet period" },
  { type: "dead", label: "Dead Period", keyword: "dead period" },
  { type: "evaluation", label: "Evaluation Period", keyword: "evaluation period" },
  { type: "shutdown", label: "Recruiting Shutdown", keyword: "shutdown" },
];

type EventRange = {
  start?: Date;
  end?: Date;
};

function parseEventRange(event: RecruitingEvent): EventRange {
  const fallbackStart = event.date ? new Date(`${event.date}T00:00:00.000Z`) : undefined;
  const description = event.description || "";
  const rangeMatch = description.match(/\(([^)]+)\)/);

  if (!rangeMatch) {
    return { start: fallbackStart };
  }

  const [rawStart, rawEnd] = rangeMatch[1].split(" - ").map((part) => part.trim());
  const parsedStart = rawStart ? new Date(rawStart) : fallbackStart;
  const parsedEnd = rawEnd ? new Date(rawEnd) : undefined;

  return {
    start: Number.isNaN(parsedStart?.getTime()) ? fallbackStart : parsedStart,
    end: Number.isNaN(parsedEnd?.getTime()) ? undefined : parsedEnd,
  };
}

function buildFallbackPeriodStatus(events: RecruitingEvent[]): PeriodStatusCard[] {
  const now = new Date();

  return periodConfig.map(({ type, label, keyword }) => {
    const related = events.filter((event) =>
      (event.title || "").toLowerCase().includes(keyword)
    );

    const ranges = related
      .map((event) => ({ event, ...parseEventRange(event) }))
      .filter((item) => item.start)
      .sort((a, b) => (a.start!.getTime() - b.start!.getTime()));

    const current = ranges.find((item) => {
      if (!item.start) return false;
      if (!item.end) return item.start <= now;
      return item.start <= now && item.end >= now;
    });

    if (current) {
      return {
        id: `fallback-${type}`,
        periodType: type,
        label,
        status: "current",
        primaryText: "Current",
        rangeText: current.end ? `${formatDate(current.start!.toISOString())} - ${formatDate(current.end.toISOString())}` : formatDate(current.start!.toISOString()),
        secondaryText: current.event.description || "Based on current NCAA key date defaults.",
      };
    }

    const upcoming = ranges.find((item) => item.start && item.start > now);
    if (upcoming) {
      return {
        id: `fallback-${type}`,
        periodType: type,
        label,
        status: "upcoming",
        primaryText: `Next starts ${formatDate(upcoming.start!.toISOString())}`,
        rangeText: upcoming.end ? `${formatDate(upcoming.start!.toISOString())} - ${formatDate(upcoming.end.toISOString())}` : formatDate(upcoming.start!.toISOString()),
        secondaryText: upcoming.event.description || "Based on NCAA key date defaults.",
      };
    }

    return {
      id: `fallback-${type}`,
      periodType: type,
      label,
      status: "inactive",
      primaryText: "No remaining dates in this cycle",
      rangeText: "",
      secondaryText: "Using default NCAA timeline guidance below.",
    };
  });
}

const RecruitingTimeline = ({
  events = [],
  athleteSport = "",
  division = "D1",
  detailed = false,
  periodStatus = [],
  sourceName,
  sourceUrl,
  emptyMessage = "No recruiting events available",
}: RecruitingTimelineProps) => {
  const [expanded, setExpanded] = useState(false);

  const filteredEvents = useMemo(() => {
    if (!athleteSport) return events;

    const sportTokens = athleteSport
      .toLowerCase()
      .split(/[,&\/]/)
      .map((token) => token.trim())
      .filter(Boolean);

    return events.filter((event) => {
      if (!event.sportType) return true;
      const eventSport = event.sportType.toLowerCase();
      return sportTokens.some((token) => eventSport.includes(token) || token.includes(eventSport));
    });
  }, [athleteSport, events]);

  const sortedEvents = useMemo(
    () => [...filteredEvents].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [filteredEvents]
  );

  const displayEvents = expanded || detailed ? sortedEvents : sortedEvents.slice(0, 3);
  const fallbackPeriodStatus = useMemo(() => buildFallbackPeriodStatus(sortedEvents), [sortedEvents]);
  const effectivePeriodStatus = periodStatus.length > 0 ? periodStatus : fallbackPeriodStatus;
  const isFallbackStatus = periodStatus.length === 0;

  const now = new Date();
  // A period already in progress has its date pinned to today (midnight UTC), which is
  // earlier than `now`, so check isCurrent first or it would be treated as past.
  const currentEventIndex = sortedEvents.findIndex((event) => event.isCurrent || new Date(event.date) >= now);

  if (!detailed) {
    return (
      <Card className="shadow-md overflow-hidden">
        <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 py-3 px-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center">
              <School className="text-primary mr-2 h-5 w-5" />
              <CardTitle className="font-montserrat font-bold text-lg text-white">Recruiting Timeline</CardTitle>
            </div>
            {sourceUrl ? (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-[#E0A458] hover:text-[#f0b467]"
              >
                NCAA Source <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </a>
            ) : null}
          </div>
        </CardHeader>

        <CardContent className="p-4">
          <h3 className="font-montserrat font-bold mb-2 text-white">Key NCAA Division I Dates</h3>

          <div className="space-y-3">
            {displayEvents.length > 0 ? (
              displayEvents.map((event, index) => (
                <div key={event.id} className="flex items-start">
                  <div
                    className={`flex-shrink-0 w-4 h-4 mt-0.5 rounded-full ${
                      index === currentEventIndex
                        ? "bg-secondary"
                        : index < (currentEventIndex === -1 ? displayEvents.length : currentEventIndex)
                          ? "bg-accent"
                          : "bg-neutral-300"
                    }`}
                  />
                  <div className="ml-3">
                    <p className="font-medium">{event.title}</p>
                    <p className="text-sm text-slate-300">{event.description || formatDate(event.date)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-2">
                <p className="text-slate-300">{emptyMessage}</p>
              </div>
            )}

            {!detailed && sortedEvents.length > 3 && (
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center text-primary w-full mt-1"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <>
                    Show less <ChevronUp className="ml-1 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Show more <ChevronDown className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>

          <Link href="/recruiting">
            <Button variant="outline" className="mt-4 mx-auto flex items-center py-2 px-8 border-[#A67C52] text-black bg-[#E0A458] hover:bg-[#f0b467]">
              View detailed recruiting plan
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-montserrat font-semibold mb-3">{division} Recruiting Calendar Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {effectivePeriodStatus.map((status) => (
            <div key={status.id} className="rounded-lg border border-[#415A77]/60 bg-[#111827] p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold">{status.label}</p>
                <Badge
                  variant="outline"
                  className={statusBadgeClass[status.periodType] || "bg-slate-100 text-slate-700"}
                >
                  {status.status === "current" ? "Current" : status.status === "upcoming" ? "Upcoming" : "Inactive"}
                </Badge>
              </div>
              <p className="mt-2 text-xs font-medium text-slate-100">{status.primaryText}</p>
              {status.rangeText ? <p className="text-xs text-slate-300">{status.rangeText}</p> : null}
              <p className="mt-2 text-xs text-slate-300">{status.secondaryText}</p>
            </div>
          ))}
        </div>
        {isFallbackStatus ? (
          <p className="mt-2 text-xs text-slate-400">Showing default NCAA timeline status from key dates.</p>
        ) : null}
      </div>

      <div>
        <h3 className="font-montserrat font-semibold mb-3">Key Recruiting Dates</h3>
        <div className="bg-[#111827] rounded-lg border border-[#415A77]/60 p-4">
          <div className="space-y-4">
            {sortedEvents.length > 0 ? (
              sortedEvents.map((event, index) => (
                <div key={event.id}>
                  <div className="flex items-start">
                    <div
                      className={`p-2 rounded-full ${
                        index === currentEventIndex
                          ? "bg-secondary/20 text-secondary"
                          : index < (currentEventIndex === -1 ? sortedEvents.length : currentEventIndex)
                            ? "bg-accent/20 text-accent"
                            : "bg-[#0B1220] text-slate-400"
                      } mr-3`}
                    >
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{event.title}</h4>
                          <p className="text-sm text-slate-300 mt-0.5">{event.description}</p>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-sm font-medium">{formatDate(event.date)}</span>
                          {event.division ? (
                            <Badge variant="outline" className="mt-1">
                              {event.division}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      {event.isNcaaEvent ? (
                        <Badge variant="outline" className="bg-primary/10 text-primary mt-2">
                          <School className="h-3 w-3 mr-1" /> NCAA Official
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  {index < sortedEvents.length - 1 ? <Separator className="my-4" /> : null}
                </div>
              ))
            ) : (
              <div className="text-center py-6">
                <School className="h-10 w-10 text-slate-500 mx-auto mb-2" />
                <p className="text-slate-300">No recruiting events available for {athleteSport || "your sport"}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {sourceName || sourceUrl ? (
        <div className="rounded-lg border border-[#415A77]/60 bg-[#0B1220] p-3 text-xs text-slate-300">
          {sourceName ? <p className="font-medium text-slate-200">Source: {sourceName}</p> : null}
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[#E0A458] hover:text-[#f0b467] inline-flex items-center mt-1">
              Open official NCAA calendar <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default RecruitingTimeline;
